import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Crown, Map, Clock, BookOpen, MessageCircleQuestion, GraduationCap, Feather } from "lucide-react";
import api from "../lib/api";
import { Button, SectionLabel, Pill } from "../components/Primitives";
import FileCard from "../components/FileCard";
import FileModal from "../components/FileModal";
import { photoSrc } from "./Stories";

const HERO = "https://images.unsplash.com/photo-1760727752323-7023aca96d34?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const LIBRARY = "https://images.unsplash.com/photo-1533029764620-40021530b1aa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const features = [
  { icon: Crown, title: "Kingdoms & Empires", desc: "Mali, Songhai, Aksum, Benin, Great Zimbabwe and more — location, culture, and contributions.", to: "/kingdoms" },
  { icon: Clock, title: "The Trans-Atlantic Trade", desc: "A documented timeline of prosperity, exploitation, and resistance across five centuries.", to: "/timeline" },
  { icon: Map, title: "The Dispersion", desc: "Trace where Africans were taken from, where they were brought, and what they built.", to: "/dispersion" },
  { icon: BookOpen, title: "Lessons for Educators", desc: "College-level lessons structured to teach un-erased history with academic rigor.", to: "/lessons" },
  { icon: GraduationCap, title: "Quizzes", desc: "Test understanding with sourced, explanatory assessments.", to: "/quizzes" },
  { icon: MessageCircleQuestion, title: "AI History Tutor", desc: "Ask any question and learn from a scholarly tutor grounded in the historical record.", to: "/tutor" },
];

export default function Landing() {
  const [kingdoms, setKingdoms] = useState([]);
  const [recent, setRecent] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [activeFile, setActiveFile] = useState(null);

  useEffect(() => {
    api.get("/kingdoms").then((r) => setKingdoms(r.data.filter((k) => k.featured).slice(0, 4))).catch(() => {});
    api.get("/stories/featured").then((r) => setFeatured(r.data && r.data.id ? r.data : null)).catch(() => {});
    api.get("/files").then((r) => {
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const fresh = r.data
        .filter((f) => f.created_at && new Date(f.created_at).getTime() >= weekAgo)
        .slice(0, 3);
      setRecent(fresh);
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative flex min-h-[88vh] items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Ancient African architecture" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
          <div className="absolute inset-0 bg-ink/30" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-6 py-24 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-3xl"
          >
            <SectionLabel>Un-erased Black History</SectionLabel>
            <h1 className="font-serif text-5xl font-semibold leading-[0.95] tracking-tight text-bone sm:text-6xl lg:text-7xl">
              Africa was never <span className="text-gold">without history.</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg leading-relaxed text-[#cfc9bd]">
              A college-level platform for parents and educators. Journey from the gold of Mali and the
              libraries of Timbuktu, through the truth of the trans-Atlantic slave trade, to the enduring
              brilliance of the global diaspora.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/kingdoms">
                <Button variant="gold" data-testid="hero-explore-btn">
                  Explore the Kingdoms <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Link to="/timeline">
                <Button variant="outline" data-testid="hero-timeline-btn">View the Timeline</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MISSION */}
      <section className="border-y border-edge bg-ash">
        <div className="mx-auto max-w-7xl px-6 py-24 md:px-12">
          <div className="grid gap-12 md:grid-cols-12">
            <div className="md:col-span-5">
              <SectionLabel>Our Purpose</SectionLabel>
              <h2 className="font-serif text-4xl font-semibold leading-tight text-bone lg:text-5xl">
                Restoring a history that was deliberately erased.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <p className="text-lg leading-relaxed text-[#cfc9bd]">
                For centuries, the majesty of African civilizations was dismissed to justify their exploitation.
                Our African Narratives confronts that erasure — not with myth, but with evidence: the coinage of Aksum, the
                manuscripts of Sankore, the bronzes of Benin, the stone cities of Zimbabwe.
              </p>
              <p className="mt-5 leading-relaxed text-muted">
                We teach the trans-Atlantic slave trade in full and honest complexity, and we follow the
                dispersed across oceans to honor what they built and endured. This is history from its roots —
                for the next generation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED KINGDOMS */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <SectionLabel>Featured</SectionLabel>
            <h2 className="font-serif text-4xl font-semibold text-bone lg:text-5xl">Kingdoms & Empires</h2>
          </div>
          <Link to="/kingdoms" className="group flex items-center gap-2 text-gold">
            <span className="text-sm tracking-wide">View all</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {kingdoms.map((k, i) => (
            <motion.div
              key={k.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <Link
                to={`/kingdoms/${k.slug}`}
                data-testid={`featured-kingdom-${k.slug}`}
                className="group block h-full overflow-hidden border border-edge bg-ink transition-colors hover:border-gold/50"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={k.image_url}
                    alt={k.name}
                    className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                </div>
                <div className="p-6">
                  <Pill tone="gold">{k.region}</Pill>
                  <h3 className="mt-3 font-serif text-2xl text-bone group-hover:text-gold">{k.name}</h3>
                  <p className="mt-2 line-clamp-2 text-sm text-muted">{k.summary}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* NEW THIS WEEK */}
      {recent.length > 0 && (
        <section className="border-t border-edge bg-ash" data-testid="new-this-week">
          <div className="mx-auto max-w-7xl px-6 py-24 md:px-12">
            <div className="mb-12 flex flex-wrap items-end justify-between gap-6">
              <div>
                <SectionLabel>Just Added</SectionLabel>
                <h2 className="font-serif text-4xl font-semibold text-bone lg:text-5xl">New This Week</h2>
                <p className="mt-3 max-w-xl text-muted">Fresh materials recently added to the curriculum library.</p>
              </div>
              <Link to="/resources" className="group flex items-center gap-2 text-gold">
                <span className="text-sm tracking-wide">Open the Library</span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {recent.map((f, i) => (
                <motion.div
                  key={f.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                >
                  <FileCard file={f} onOpen={setActiveFile} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURED HERITAGE STORY */}
      {featured && (
        <section className="border-t border-edge" data-testid="featured-story">
          <div className="mx-auto max-w-7xl px-6 py-24 md:px-12">
            <div className="grid items-center gap-10 md:grid-cols-2">
              <div className={featured.photo_url ? "" : "md:col-span-2 md:max-w-2xl"}>
                <SectionLabel>{featured.story_of_month ? `Story of the Month — ${featured.story_of_month}` : "Heritage Story"}</SectionLabel>
                <h2 className="font-serif text-4xl font-semibold leading-tight text-bone lg:text-5xl">{featured.title}</h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Pill tone="gold">{featured.year_label}</Pill>
                  <Pill tone="muted">{featured.region}</Pill>
                  {featured.story_of_month && featured.like_count > 0 && <Pill tone="blood" data-testid="som-hearts">♥ {featured.like_count} most-hearted</Pill>}
                </div>
                <p className="mt-6 line-clamp-4 leading-relaxed text-[#cfc9bd]">{featured.body}</p>
                <p className="mt-3 text-sm text-muted">— {featured.author_name}</p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link to={featured.id ? `/stories/${featured.id}` : "/stories"}><Button variant="gold" data-testid="featured-read-btn">Read this story <ArrowRight className="h-4 w-4" /></Button></Link>
                  <Link to="/stories"><Button variant="outline" data-testid="featured-share-btn"><Feather className="h-4 w-4" /> Share your own</Button></Link>
                </div>
              </div>
              {featured.photo_url && (
                <div className="overflow-hidden border border-edge">
                  <img src={photoSrc(featured.photo_url)} alt="" className="h-full max-h-96 w-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* PREMIUM CTA */}
      <section className="border-t border-edge bg-ash" data-testid="premium-cta-section">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-20 md:flex-row md:items-center md:px-12">
          <div className="max-w-xl">
            <SectionLabel>Support Our African Narratives</SectionLabel>
            <h2 className="font-serif text-4xl font-semibold text-bone">Go Premium for $3/month</h2>
            <p className="mt-4 text-muted">
              Unlock the full curriculum — Lessons, Quizzes, Assignments, the AI Tutor and Certificates.
              The Globe, Dispersion, Stories, Timeline and Kingdoms stay free for everyone.
            </p>
          </div>
          <Link to="/premium">
            <Button variant="gold" data-testid="landing-premium-btn">
              <Crown className="h-4 w-4" /> Go Premium
            </Button>
          </Link>
        </div>
      </section>

      {/* EDUCATOR CTA */}
      <section className="relative overflow-hidden border-y border-edge">
        <img src={LIBRARY} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-ink/80" />
        <div className="relative mx-auto max-w-7xl px-6 py-24 text-center md:px-12">
          <SectionLabel>For Parents & Educators</SectionLabel>
          <h2 className="mx-auto max-w-2xl font-serif text-4xl font-semibold text-bone lg:text-5xl">
            Teach the history that was kept from us.
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-muted">
            Save lessons, track learners, and ask the AI tutor anything. Create a free account to begin.
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Link to="/lessons">
              <Button variant="gold" data-testid="cta-lessons-btn">Browse Lessons</Button>
            </Link>
            <Link to="/tutor">
              <Button variant="outline" data-testid="cta-tutor-btn">Ask the Tutor</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURE GRID */}
      <section className="mx-auto max-w-7xl px-6 py-24 md:px-12">
        <div className="grid gap-px overflow-hidden border border-edge bg-edge sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <Link
              key={f.title}
              to={f.to}
              data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="group bg-ink p-8 transition-colors hover:bg-ash"
            >
              <f.icon className="h-7 w-7 text-gold" />
              <h3 className="mt-5 font-serif text-2xl text-bone group-hover:text-gold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted">{f.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      {activeFile && <FileModal file={activeFile} onClose={() => setActiveFile(null)} />}
    </div>
  );
}
